import Vacancy from "../models/Vacancy";
import axios from "axios";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../utils/firebase";
import { getAuth, getIdToken } from "firebase/auth";
import { authStateReady } from "../utils/authState";

const API_URL_ALL = "https://europe-west3-hyper-e122e.cloudfunctions.net/get_vacancies";
const API_URL_ID = "https://europe-west3-hyper-e122e.cloudfunctions.net/get_vacancy_by_id";
const API_URL_RESPONSE = "https://europe-west3-hyper-e122e.cloudfunctions.net/create_response"; 
const API_URL_FAVORITE_TOGGLE = "https://europe-west3-hyper-e122e.cloudfunctions.net/toggle_vacancy_favorite"; 

class VacancyService {
  constructor() {
    this.vacancies = [];
    this.nextId = null;
    this.isLoading = false;
  }

async waitForAuthUser() {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

async fetchVacancies(limit = 12, selectedSpecifics = [], selectedRemote = 'all', cityId = null, selectedRegion = 'all') {
  if (this.isLoading) return { vacancies: [], total_count: 0 };
  this.isLoading = true;

  try {
    const user = await this.waitForAuthUser();

    if (!user) throw new Error("Пользователь не авторизован");

    const token = await user.getIdToken();

    const baseUrl = new URL(API_URL_ALL);
    baseUrl.searchParams.set('limit', limit);

    if (this.nextId) {
      baseUrl.searchParams.set('last_id', this.nextId);
    }

    if (selectedRemote === 'remote-only') {
      baseUrl.searchParams.set('is_remote', 'remote-only');
    }

    if (cityId) {
      baseUrl.searchParams.set('city_id', cityId);
    }

    if (selectedRegion === 'local') {
      baseUrl.searchParams.set('my_region_only', 'true');
    } else {
      baseUrl.searchParams.set('my_region_only', 'false');
    }

    if (selectedSpecifics.length > 0) {
      selectedSpecifics.forEach((spec) => {
        baseUrl.searchParams.append('specifics', spec);
      });
    }

    console.log("🌐 Отправляем запрос на вакансии:", baseUrl.toString());

    const response = await fetch(baseUrl.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) throw new Error(`Ошибка загрузки: ${response.statusText}`);

    const data = await response.json();

    const parsedVacancies = data.vacancies.map(Vacancy.fromJson);
    this.vacancies = [...this.vacancies, ...parsedVacancies];
    this.nextId = data.next_id || null;

    console.log(`✅ Загружено ${parsedVacancies.length} вакансий (всего: ${data.total_count})`);

    return {
      vacancies: parsedVacancies,
      total_count: data.total_count || 0
    };
  } catch (error) {
    console.error('❌ Ошибка при загрузке вакансий:', error);
    return { vacancies: [], total_count: 0 };
  } finally {
    this.isLoading = false;
  }
}
  
async fetchVacancyById(vacancyId, userId = null) {
  try {
    const user = await this.waitForAuthUser();

    const headers = {
      "Content-Type": "application/json"
    };

    let url =
      `${API_URL_ID}?vacancy_id=${encodeURIComponent(vacancyId)}`;


    /*
     * Авторизованный пользователь:
     * добавляем token и user_id.
     */

    if (user) {

      const token =
        await user.getIdToken();

      headers.Authorization =
        `Bearer ${token}`;


      const resolvedUserId =
        userId || user.uid;


      if (resolvedUserId) {

        url +=
          `&user_id=${encodeURIComponent(resolvedUserId)}`;

      }

    } else {

      console.log(
        "🌍 Загружаем вакансию в публичном режиме"
      );

    }


    const response =
      await axios.get(
        url,
        {
          headers
        }
      );


    const vacancyData =
      response.data?.vacancy;


    if (!vacancyData) {

      throw new Error(
        "Вакансия не найдена"
      );

    }


    return Vacancy.fromJson(
      vacancyData
    );

  } catch (error) {

    console.error(
      "❌ Ошибка при загрузке вакансии:",
      error?.response?.data ||
      error?.message ||
      error
    );


    return null;

  }
}

/**
 * 🔹 Функция отклика на вакансию
 * @param {string} vacancyId - ID вакансии
 * @returns {Promise<boolean>} - Успех или неудача запроса
 */
async createResponse(vacancyId) {

  try {
    const user = await authStateReady();
    if (!user) {
      throw new Error("Пользователь не авторизован");
    }

    const token = await user.getIdToken(true);
    const response = await axios.post(

      API_URL_RESPONSE,
      {
        user_id: user.uid,
        vacancy_id: vacancyId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.data.success) {
      console.log("✅ Отклик успешно создан");
      return true;
    }
    console.warn("⚠️ Ошибка при отклике:", response.data.error);
    return false;
  } catch (error) {
    console.error("❌ Ошибка при отправке отклика:", error);
    return false;
  }
}

  /**
   * 🔁 Добавление или удаление вакансии из избранного
   * @param {string} vacancyId - ID вакансии
   * @returns {Promise<{ success: boolean, isFavorite: boolean, action: string }>}
   */
  async toggleFavorite(vacancyId) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("Пользователь не авторизован");

      const token = await getIdToken(user, true);
      const url = `${API_URL_FAVORITE_TOGGLE}`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: user.uid,
          vacancy_id: vacancyId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        console.warn("⚠️ Ошибка при изменении избранного:", result.error);
        return { success: false };
      }

      console.log(`🔁 Избранное обновлено: ${result.action}`);
      return {
        success: true,
        isFavorite: result.is_favorite,
        action: result.action,
      };
    } catch (error) {
      console.error("❌ Ошибка в toggleFavorite:", error);
      return { success: false };
    }
  }

  async deleteVacancy(vacancyId) {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) throw new Error("❌ Пользователь не авторизован");

      const token = await currentUser.getIdToken(true);
      const payload = { vacancy_id: vacancyId, is_admin: true };

      console.log("🗑 Отправляем запрос на удаление вакансии:", payload);

      const response = await axios.post(`https://europe-west3-hyper-e122e.cloudfunctions.net/delete_vacancy`, payload, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      console.log("✅ Вакансия успешно удалена:", response.data);
      return response.data;

    } catch (error) {
      console.error("❌ Ошибка при удалении вакансии:", error.response?.data || error.message);
      return { success: false, error: error.message };
    }
  }
  
}

export default new VacancyService();